/**
 * PlayersStep — Compact player details form
 *
 * Accessibility: htmlFor/id on all label/input pairs (unique per player index),
 * aria-pressed on option chip toggle buttons, aria-label on remove button.
 */

import { Plus, Minus, User } from '../components/icons'
import type { PlayerInfo } from '../types'

interface PlayersStepProps {
  players: PlayerInfo[]
  playerCount: number
  maxPlayers: number
  onUpdatePlayer: (index: number, player: PlayerInfo) => void
  onAddPlayer: () => void
  onRemovePlayer: (index: number) => void
  requirePhone?: boolean
  hideCart?: boolean
}

function PlayerCard({
  player, index, onUpdate, onRemove, canRemove, requirePhone, hideCart,
}: {
  player: PlayerInfo; index: number
  onUpdate: (player: PlayerInfo) => void
  onRemove: () => void; canRemove: boolean
  requirePhone?: boolean
  hideCart?: boolean
}) {
  const handleChange = (field: string, value: string) => {
    if (field.startsWith('homeClub.')) {
      const hcField = field.replace('homeClub.', '')
      onUpdate({ ...player, homeClub: { ...player.homeClub, [hcField]: value } })
    } else if (field.startsWith('bookingOptions.')) {
      const optField = field.replace('bookingOptions.', '') as keyof typeof player.bookingOptions
      onUpdate({
        ...player,
        bookingOptions: { ...player.bookingOptions, [optField]: !player.bookingOptions[optField] },
      })
    } else {
      onUpdate({ ...player, [field]: value })
    }
  }

  const prefix = `bw2-player-${index}`
  const playerLabel = player.isPrimary ? 'Your Details' : `Player ${index + 1}`

  return (
    <div className="bw2-player-card" role="group" aria-label={playerLabel}>
      <div className="bw2-player-card__header">
        <div className="bw2-player-card__number">
          <div className="bw2-player-card__badge" aria-hidden="true">{index + 1}</div>
          <span className="bw2-player-card__label">
            {playerLabel}
          </span>
          {player.isPrimary && <span className="bw2-player-card__primary-tag">Primary</span>}
        </div>
        {canRemove && (
          <button
            className="bw2-btn bw2-btn--ghost"
            onClick={onRemove}
            type="button"
            aria-label={`Remove player ${index + 1}`}
          >
            <Minus size={10} />
          </button>
        )}
      </div>

      <div className="bw2-form-grid">
        <div className="bw2-form-field">
          <label className="bw2-form-label" htmlFor={`${prefix}-firstName`}>First Name *</label>
          <input
            className="bw2-form-input"
            id={`${prefix}-firstName`}
            type="text"
            value={player.firstName}
            onInput={e => handleChange('firstName', (e.target as HTMLInputElement).value)}
            placeholder="James"
            autoComplete="given-name"
            aria-required="true"
          />
        </div>
        <div className="bw2-form-field">
          <label className="bw2-form-label" htmlFor={`${prefix}-lastName`}>Last Name *</label>
          <input
            className="bw2-form-input"
            id={`${prefix}-lastName`}
            type="text"
            value={player.lastName}
            onInput={e => handleChange('lastName', (e.target as HTMLInputElement).value)}
            placeholder="Anderson"
            autoComplete="family-name"
            aria-required="true"
          />
        </div>
      </div>

      {player.isPrimary ? (
        <div className="bw2-form-grid" style={{ marginTop: '6px' }}>
          <div className="bw2-form-field">
            <label className="bw2-form-label" htmlFor={`${prefix}-email`}>Email *</label>
            <input
              className="bw2-form-input"
              id={`${prefix}-email`}
              type="email"
              value={player.email}
              onInput={e => handleChange('email', (e.target as HTMLInputElement).value)}
              placeholder="james@email.com"
              autoComplete="email"
              aria-required="true"
            />
          </div>
          <div className="bw2-form-field">
            <label className="bw2-form-label" htmlFor={`${prefix}-homeClub`}>Home Club</label>
            <input
              className="bw2-form-input"
              id={`${prefix}-homeClub`}
              type="text"
              value={player.homeClub.name}
              onInput={e => handleChange('homeClub.name', (e.target as HTMLInputElement).value)}
              placeholder="Kingston Heath"
            />
          </div>
        </div>
      ) : (
        <div style={{ marginTop: '6px' }}>
          <div className="bw2-form-field">
            <label className="bw2-form-label" htmlFor={`${prefix}-email`}>Email</label>
            <input
              className="bw2-form-input"
              id={`${prefix}-email`}
              type="email"
              value={player.email}
              onInput={e => handleChange('email', (e.target as HTMLInputElement).value)}
              placeholder="player@email.com"
              autoComplete="email"
            />
          </div>
        </div>
      )}

      {/* Phone field — shown when requirePhone is true */}
      {requirePhone && (
        <div className="bw2-form-grid" style={{ marginTop: '6px' }}>
          <div className="bw2-form-field">
            <label className="bw2-form-label" htmlFor={`${prefix}-phone`}>Phone {player.isPrimary ? '*' : ''}</label>
            <input
              className="bw2-form-input"
              id={`${prefix}-phone`}
              type="tel"
              value={player.phone || ''}
              onInput={e => handleChange('phone', (e.target as HTMLInputElement).value)}
              placeholder="+61 4XX XXX XXX"
              autoComplete="tel"
              aria-required={player.isPrimary ? 'true' : undefined}
            />
          </div>
        </div>
      )}

      <div className="bw2-options" role="group" aria-label="Booking options">
        <button
          className={`bw2-option-chip ${player.bookingOptions.caddie ? 'bw2-option-chip--active' : ''}`}
          onClick={() => handleChange('bookingOptions.caddie', '')}
          type="button"
          aria-pressed={player.bookingOptions.caddie}
        >
          <User size={10} /> Caddie
        </button>
        <button
          className={`bw2-option-chip ${player.bookingOptions.clubHire ? 'bw2-option-chip--active' : ''}`}
          onClick={() => handleChange('bookingOptions.clubHire', '')}
          type="button"
          aria-pressed={player.bookingOptions.clubHire}
        >
          <span aria-hidden="true">🏌️</span> Clubs
        </button>
        {!hideCart && (
          <button
            className={`bw2-option-chip ${player.bookingOptions.cart ? 'bw2-option-chip--active' : ''}`}
            onClick={() => handleChange('bookingOptions.cart', '')}
            type="button"
            aria-pressed={player.bookingOptions.cart}
          >
            <span aria-hidden="true">🛒</span> Cart
          </button>
        )}
      </div>

      {/* Club hire sub-options — shaft type & hand preference */}
      {player.bookingOptions.clubHire && (
        <div className="bw2-club-hire-options" role="group" aria-label="Club hire preferences">
          <div className="bw2-club-hire-row">
            <span className="bw2-club-hire-label">Shaft</span>
            <div className="bw2-club-hire-toggles">
              <button
                className={`bw2-mini-chip ${(player.bookingOptions.clubHireShaft || 'steel') === 'steel' ? 'bw2-mini-chip--active' : ''}`}
                onClick={() => onUpdate({ ...player, bookingOptions: { ...player.bookingOptions, clubHireShaft: 'steel' } })}
                type="button"
                aria-pressed={(player.bookingOptions.clubHireShaft || 'steel') === 'steel'}
              >
                Steel
              </button>
              <button
                className={`bw2-mini-chip ${player.bookingOptions.clubHireShaft === 'graphite' ? 'bw2-mini-chip--active' : ''}`}
                onClick={() => onUpdate({ ...player, bookingOptions: { ...player.bookingOptions, clubHireShaft: 'graphite' } })}
                type="button"
                aria-pressed={player.bookingOptions.clubHireShaft === 'graphite'}
              >
                Graphite
              </button>
            </div>
          </div>
          <div className="bw2-club-hire-row">
            <span className="bw2-club-hire-label">Hand</span>
            <div className="bw2-club-hire-toggles">
              <button
                className={`bw2-mini-chip ${(player.bookingOptions.clubHireHand || 'right') === 'right' ? 'bw2-mini-chip--active' : ''}`}
                onClick={() => onUpdate({ ...player, bookingOptions: { ...player.bookingOptions, clubHireHand: 'right' } })}
                type="button"
                aria-pressed={(player.bookingOptions.clubHireHand || 'right') === 'right'}
              >
                Right
              </button>
              <button
                className={`bw2-mini-chip ${player.bookingOptions.clubHireHand === 'left' ? 'bw2-mini-chip--active' : ''}`}
                onClick={() => onUpdate({ ...player, bookingOptions: { ...player.bookingOptions, clubHireHand: 'left' } })}
                type="button"
                aria-pressed={player.bookingOptions.clubHireHand === 'left'}
              >
                Left
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export function PlayersStep({ players, playerCount, maxPlayers, onUpdatePlayer, onAddPlayer, onRemovePlayer, requirePhone, hideCart }: PlayersStepProps) {
  return (
    <div>
      <div className="bw2-section-label" aria-live="polite">
        Players · {playerCount}/{maxPlayers}
      </div>

      {players.map((player, index) => (
        <PlayerCard
          key={player.id}
          player={player}
          index={index}
          onUpdate={(updated) => onUpdatePlayer(index, updated)}
          onRemove={() => onRemovePlayer(index)}
          canRemove={!player.isPrimary}
          requirePhone={requirePhone}
          hideCart={hideCart}
        />
      ))}

      {playerCount < maxPlayers && (
        <button className="bw2-btn bw2-btn--secondary bw2-btn--full" onClick={onAddPlayer} type="button" style={{ marginTop: '4px' }}>
          <Plus size={12} /> Add Player
        </button>
      )}
    </div>
  )
}
